import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, Dog, ShoppingBag, CalendarClock, Scissors, Receipt, X, ShieldCheck, MessageSquareText, Building2, Inbox, ClipboardList, Users } from 'lucide-react';
import theme from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import { canShowLink } from '../../utils/accessControl';
import { ROLES } from '../../constants/roles';

/**
 * Sidebar component with navigation menu.
 * Shows when user is authenticated.
 */
const Sidebar = ({ isMobile = false, isOpen = false, onClose }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const canSeeAdminSection = canShowLink(user?.roles || [], { minimumRole: ROLES.ADMIN });
  const canSeeSuperAdminSection = canShowLink(user?.roles || [], { minimumRole: ROLES.SUPER_ADMIN });
  
  const menuItems = [
    { 
      path: '/app/inventory', 
      label: t('sidebar.inventory'), 
      icon: Package 
    },
    { 
      path: '/app/pets', 
      label: t('sidebar.pets'), 
      icon: Dog 
    },
    {
      path: '/app/sales',
      label: t('sidebar.sales'),
      icon: ShoppingBag
    },
    {
      path: '/app/invoices',
      label: t('sidebar.invoices', 'Invoices'),
      icon: Receipt
    },
    {
      path: '/app/daycare',
      label: t('sidebar.daycare'),
      icon: CalendarClock
    },
    {
      path: '/app/grooming',
      label: t('sidebar.grooming'),
      icon: Scissors
    }
  ];

  const adminMenuItems = [
    {
      path: '/app/orders',
      label: t('sidebar.storefrontOrders', 'Storefront Orders'),
      icon: ClipboardList,
    },
    {
      path: '/app/storefront-customers',
      label: t('sidebar.storefrontCustomers', 'Storefront Customers'),
      icon: Users,
    },
    {
      path: '/app/admin',
      label: t('navbar.adminUserManagement', 'User Management'),
      icon: ShieldCheck,
    },
    {
      path: '/app/contacts',
      label: t('navbar.adminContacts', 'Contact Messages'),
      icon: MessageSquareText,
    },
    {
      path: '/app/widget',
      label: t('widget.admin.title', 'Widget Submissions'),
      icon: Inbox,
    },
    ...(canSeeSuperAdminSection
      ? [{
          path: '/app/platform',
          label: t('navbar.adminPlatformTenants', 'Platform Tenants'),
          icon: Building2,
        }]
      : []),
  ];

  const styles = {
    sidebar: {
      width: isMobile ? '280px' : '250px',
      backgroundColor: theme.colors.primary.main,
      minHeight: isMobile ? '100vh' : 'calc(100vh - 60px)',
      padding: `${theme.spacing.lg} 0`,
      boxShadow: theme.shadows.lg,
      position: isMobile ? 'fixed' : 'relative',
      top: isMobile ? 0 : 'auto',
      left: isMobile ? 0 : 'auto',
      zIndex: isMobile ? 1100 : 'auto',
      transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
      transition: theme.transitions.normal,
      overflowY: 'auto',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      color: theme.colors.primary.contrast,
      textDecoration: 'none',
      transition: theme.transitions.fast,
      borderLeft: `4px solid transparent`,
      opacity: 0.85
    },
    menuItemActive: {
      backgroundColor: theme.colors.primary.dark,
      borderLeftColor: theme.colors.accent.coral,
      opacity: 1,
      fontWeight: theme.typography.fontWeight.medium
    },
    menuItemHover: {
      backgroundColor: theme.colors.primary.dark,
      opacity: 1
    },
    icon: {
      marginRight: theme.spacing.md,
      width: '20px',
      height: '20px'
    },
    label: {
      fontSize: theme.typography.fontSize.base
    },
    mobileHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${theme.spacing.sm} ${theme.spacing.lg} ${theme.spacing.md}`,
      borderBottom: '1px solid rgba(255,255,255,0.15)',
      marginBottom: theme.spacing.sm,
      color: theme.colors.primary.contrast,
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
    },
    closeButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: `1px solid rgba(255,255,255,0.35)`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: 'transparent',
      color: theme.colors.primary.contrast,
      cursor: 'pointer',
    },
    sectionHeader: {
      margin: `${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.sm}`,
      fontSize: theme.typography.fontSize.xs,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: 'rgba(255,255,255,0.72)',
      fontWeight: theme.typography.fontWeight.bold,
    },
  };

  return (
    <div
      style={styles.sidebar}
      role={isMobile ? 'dialog' : 'navigation'}
      aria-modal={isMobile ? true : undefined}
      aria-label={t('sidebar.navigation', 'Navigation')}
    >
      {isMobile && (
        <div style={styles.mobileHeader}>
          <span>{t('sidebar.navigation', 'Navigation')}</span>
          <button
            style={styles.closeButton}
            onClick={onClose}
            aria-label={t('sidebar.closeMenu', 'Close navigation menu')}
          >
            <X size={18} />
          </button>
        </div>
      )}
      {menuItems.map(item => {
        const IconComponent = item.icon;
        const isActive = location.pathname === item.path;
        
        return (
          <NavLink
            key={item.path}
            to={item.path}
            style={{
              ...styles.menuItem,
              ...(isActive ? styles.menuItemActive : {})
            }}
            onClick={() => {
              if (isMobile && onClose) {
                onClose();
              }
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                Object.assign(e.currentTarget.style, styles.menuItemHover);
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.opacity = '0.85';
              }
            }}
          >
            <IconComponent style={styles.icon} />
            <span style={styles.label}>{item.label}</span>
          </NavLink>
        );
      })}

      {isMobile && canSeeAdminSection && (
        <>
          <div style={styles.sectionHeader}>{t('navbar.admin', 'Admin')}</div>
          {adminMenuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                style={{
                  ...styles.menuItem,
                  ...(isActive ? styles.menuItemActive : {}),
                }}
                onClick={() => {
                  if (isMobile && onClose) {
                    onClose();
                  }
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    Object.assign(e.currentTarget.style, styles.menuItemHover);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.opacity = '0.85';
                  }
                }}
              >
                <IconComponent style={styles.icon} />
                <span style={styles.label}>{item.label}</span>
              </NavLink>
            );
          })}
        </>
      )}
    </div>
  );
};

export default Sidebar;
